// ============================================================================
// PROJET MOPPER — store local partagé entre les outils (/outils/*)
// Persistance localStorage (fonctionne connecté ou non). Aucun backend.
// Les composants s'abonnent via useProject() et restent synchronisés grâce à
// un évènement window custom.
// ============================================================================
import { useCallback, useEffect, useState } from "react";
import { objectById, customVolumeM3, VOLUME_MARGIN } from "@/lib/toolsConfig";

const KEY = "mopper_project_v1";
const EVT = "mopper-project-change";

export function emptyProject() {
  return {
    id: `p_${Date.now().toString(36)}`,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    volume: { qty: {}, custom: [] }, // qty: {itemId: number}, custom: [{id,name,l,w,h,qty}]
    boxes: null, // { inputs, result }
    inventory: { rooms: [], containers: [], items: [] },
    move: {}, // { from, to, dateISO, ... } — réservé
  };
}

export function loadProject() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (!p || typeof p !== "object") return null;
    // garde-fous
    p.volume = p.volume || { qty: {}, custom: [] };
    p.volume.qty = p.volume.qty || {};
    p.volume.custom = p.volume.custom || [];
    p.inventory = p.inventory || { rooms: [], containers: [], items: [] };
    p.inventory.rooms = p.inventory.rooms || [];
    p.inventory.containers = p.inventory.containers || [];
    p.inventory.items = p.inventory.items || [];
    return p;
  } catch {
    return null;
  }
}

export function saveProject(p) {
  try {
    p.updatedAt = new Date().toISOString();
    localStorage.setItem(KEY, JSON.stringify(p));
    window.dispatchEvent(new CustomEvent(EVT));
  } catch {
    /* quota / privacy mode — silencieux */
  }
  return p;
}

export function clearProject() {
  try {
    localStorage.removeItem(KEY);
    window.dispatchEvent(new CustomEvent(EVT));
  } catch { /* noop */ }
}

// Hook réactif. ensure=true crée un projet vide si aucun n'existe.
export function useProject(ensure = false) {
  const [project, setProject] = useState(() => {
    const p = loadProject();
    return p || (ensure ? emptyProject() : null);
  });

  useEffect(() => {
    const sync = () => setProject(loadProject() || (ensure ? emptyProject() : null));
    window.addEventListener(EVT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(EVT, sync);
      window.removeEventListener("storage", sync);
    };
  }, [ensure]);

  // update(fn) : reçoit un draft (projet existant ou nouveau), le mute/retourne, sauvegarde.
  const update = useCallback((fn) => {
    const current = loadProject() || emptyProject();
    const draft = JSON.parse(JSON.stringify(current));
    const next = fn(draft) || draft;
    saveProject(next);
    setProject(next);
    return next;
  }, []);

  const reset = useCallback(() => {
    clearProject();
    setProject(ensure ? emptyProject() : null);
  }, [ensure]);

  return { project, update, reset };
}

// ---- SÉLECTEURS (dérivés) ---------------------------------------------------
export function getVolumeTotal(project) {
  if (!project?.volume) return 0;
  let raw = 0;
  for (const [key, q] of Object.entries(project.volume.qty || {})) {
    const id = key.includes("::") ? key.split("::")[1] : key;
    const o = objectById(id);
    if (o) raw += (Number(q) || 0) * o.v;
  }
  for (const c of project.volume.custom || []) {
    raw += customVolumeM3(c.l, c.w, c.h, c.qty);
  }
  return Math.round(raw * VOLUME_MARGIN * 10) / 10;
}

export function getVolumeByRoom(project) {
  const out = {};
  if (!project?.volume) return out;
  for (const [key, q] of Object.entries(project.volume.qty || {})) {
    if (!key.includes("::")) continue;
    const [room, id] = key.split("::");
    const o = objectById(id);
    if (!o) continue;
    out[room] = (out[room] || 0) + (Number(q) || 0) * o.v * VOLUME_MARGIN;
  }
  for (const c of project.volume.custom || []) {
    const room = c.room || "autre";
    out[room] = (out[room] || 0) + customVolumeM3(c.l, c.w, c.h, c.qty) * VOLUME_MARGIN;
  }
  Object.keys(out).forEach((k) => { out[k] = Math.round(out[k] * 10) / 10; });
  return out;
}

export function getVolumeItemCount(project) {
  if (!project?.volume) return 0;
  let n = 0;
  for (const q of Object.values(project.volume.qty || {})) n += Number(q) || 0;
  for (const c of project.volume.custom || []) n += Number(c.qty) || 0;
  return n;
}

export function getInventoryCounts(project) {
  const inv = project?.inventory;
  if (!inv) return { rooms: 0, containers: 0, items: 0, units: 0 };
  const units = (inv.items || []).reduce((s, it) => s + (Number(it.qty) || 1), 0);
  return {
    rooms: (inv.rooms || []).length,
    containers: (inv.containers || []).length,
    items: (inv.items || []).length,
    units,
  };
}

export function getBoxTotal(project) {
  return project?.boxes?.result?.total || 0;
}

// ---- TRACKING (léger, non bloquant) -----------------------------------------
// Pousse vers window.dataLayer si présent, sinon no-op. Aucune donnée perso.
export function track(event, props = {}) {
  try {
    if (typeof window === "undefined") return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...props });
  } catch { /* noop */ }
}
